import React, { useState, useRef, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule, themeQuartz } from 'ag-grid-community';
import { TreeDataModule } from 'ag-grid-enterprise';
import { OmniDropdownComponent, OmniIconComponent, Utils } from '../../../../../shared';
import { PlacementDetailsTableService } from "../../../../placements";

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule, TreeDataModule]);

const CreateMediaOrderWizard = ({ isOpen, onClose, onSubmit, campaignTaxonomyData, campaignData, systemParametersData, clientCodes }) => {

    const [currentStep, setCurrentStep] = useState(1);
    const gridRef = useRef(null);
    const [taxonomyPreview, setTaxonomyPreview] = useState('');
    const [selectedTotal, setSelectedTotal] = useState(0);
    const [selectedChannelData, setSelectedChannelData] = useState([]);

    const localCurrency = campaignData[0]?.local_currency_name;
    const isLocal = systemParametersData?.data?.isLocal;

    const getParentTaxonomyData = () => {
        let campaign = campaignData[0];
        let taxonomyJson = campaign?.taxonomy ? campaign?.taxonomy : null;
        let campaignTaxonomy = campaign?.campaign_taxonomy?.split(':|');
        let taxonomyName = campaignTaxonomy[0];
        let taxonomySchema = campaign?.taxonomy_schema ? campaign?.taxonomy_schema : null;
        let taxonomyData = campaignTaxonomyData?.fields;
        
        return PlacementDetailsTableService.splitPlacementStringIntoSchemaObject(taxonomyName, taxonomySchema, taxonomyData, '', null, taxonomyJson);
    };
    
    // Initialize form data dynamically based on taxonomy fields
    const initializeFormData = () => {
        const initialData = {
            flight: {
                startDate: campaignData[0]?.start_date || '',
                endDate: campaignData[0]?.end_date || ''
            },
            planned: '',
            ordered: '',
            costType: '',
            clientCode: campaignData[0]?.client_code,
            orderBudget: ''
        };
        let taxonomyData = getParentTaxonomyData();
        
        // Add dynamic fields from campaignTaxonomyData
        if (campaignTaxonomyData?.fields) {
            campaignTaxonomyData.fields.forEach(field => {
                initialData[field.alias || field.name] = taxonomyData[field.alias || field.name] || "NU";
            });
        }
        
        return initialData;
    };

    const [formData, setFormData] = useState(() => initializeFormData());

    // Transform channel data for AG Grid
    const transformChannelData = () => {
        const channels = campaignData[0]?.media_attributes?.channels;
        if (!channels || !Array.isArray(channels)) return [];

        return channels.map((channel, channelIndex) => {
            // Format date range for channel
            const channelStartDate = channel.channel_startdate ? new Date(channel.channel_startdate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }) : '';
            const channelEndDate = channel.channel_enddate ? new Date(channel.channel_enddate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }) : '';
            const channelDateRange = channelStartDate && channelEndDate ? `${channelStartDate} - ${channelEndDate}` : '';

            // Transform publishers (nestedData)
            const publishers = channel.nestedData?.map((publisher, pubIndex) => {
                const pubStartDate = publisher.publisher_start_date ? new Date(publisher.publisher_start_date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }) : '';
                const pubEndDate = publisher.publisher_end_date ? new Date(publisher.publisher_end_date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }) : '';
                const pubDateRange = pubStartDate && pubEndDate ? `${pubStartDate} - ${pubEndDate}` : publisher.flight || '';

                return {
                    id: `publisher-${channelIndex}-${pubIndex}`,
                    channel: publisher.publisher_display_name || publisher.publisher_name || publisher.description,
                    date: pubDateRange,
                    availablePlannedSpend: localCurrency && isLocal ? publisher.publisher_budget_in_pmds_local : publisher.publisher_budget_in_pmds || 0,
                    isChannel: false,
                    // Keep original data for reference
                    originalData: publisher
                };
            }) || [];

            return {
                id: `channel-${channelIndex}`,
                channel: channel.channel_display_name || channel.channel_name,
                date: channelDateRange,
                availablePlannedSpend: localCurrency && isLocal ? channel.channel_total_budget_in_pmds_local : channel.channel_total_budget_in_pmds || 0,
                isChannel: true,
                nestedData: publishers,
                // Keep original data for reference
                originalData: channel
            };
        });
    };

    const gridChannelData = transformChannelData();

    const totalSteps = 3;

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value?.id ? value.id : value
        }));
    };

    const handleFlightChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            flight: {
                ...prev.flight,
                [field]: value
            }
        }));
    };

    const handleNext = () => {
        if (currentStep < totalSteps) {
            setCurrentStep(prev => prev + 1);
        }
    };

    const handlePrevious = () => {
        if (currentStep > 1) {
            setCurrentStep(prev => prev - 1);
        }
    };

    const handleSubmit = () => {
        onSubmit(formData);
    };

    // Restore grid selection when grid is ready
    const onGridReady = React.useCallback((params) => {
        if (selectedChannelData.length > 0) {
            const selectedIds = selectedChannelData.map(item => item.id);
            params.api.forEachNode((node) => {
                if (selectedIds.includes(node.data?.id)) {
                    node.setSelected(true);
                }
            });
        }
    }, [selectedChannelData]);

    // Generate taxonomy preview based on form data
    const generateTaxonomyPreview = () => {
        if (!campaignTaxonomyData?.fields) return '';
        
        const parts = campaignTaxonomyData.fields.map((field, index) => {
            const value = formData[field.alias || field.name];
            if (value && !field.hidden) {
                if(index === campaignTaxonomyData.fields.length - 1){
                    return value;
                }
                return value + field.delimiter_value;    
            }
            return '';
        });
        
        return parts.filter(p => p).join('');
    };

    // Update preview whenever formData changes
    React.useEffect(() => {
        setTaxonomyPreview(generateTaxonomyPreview());
    }, [formData]);

    // Column definitions for AG Grid (defined outside render to avoid recreation)
    const columnDefs = React.useMemo(() => [
        {
            field: 'channel',
            headerName: 'CHANNEL',
            cellRenderer: 'agGroupCellRenderer',
            showRowGroup: true,
            cellRendererParams: {
                suppressCount: true,
            },
            flex: 2
        },
        {
            field: 'date',
            headerName: 'DATE',
            flex: 1.5,
            cellStyle: (params) => {
                if (params.data?.isChannel && (!params.data?.date || params.data?.availablePlannedSpend === 0)) {
                    return { color: '#d3d3d3' };
                }
                return null;
            }
        },
        {
            field: 'availablePlannedSpend',
            headerName: 'AVAILABLE PLANNED SPEND',
            flex: 1.5,
            cellStyle: (params) => {
                if (params.value === 0) {
                    return { color: '#d3d3d3' };
                }
                return null;
            },
            cellClass: 'ag-right-aligned-cell',
            valueFormatter: (params) => {
                if (params.value === null || params.value === undefined) return '';
                const currency = localCurrency && isLocal ? localCurrency : 'USD';
                return params.value.toLocaleString('en-US', {
                    style: 'currency',
                    currency: currency,
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
            }
        }
    ], [localCurrency, isLocal]);

    const defaultColDef = React.useMemo(() => ({
        sortable: false,
        resizable: true,
        filter: false,
        suppressHeaderMenuButton: true
    }), []);

    const channelTableTheme = React.useMemo(() => themeQuartz.withParams({
        headerRowBorder: false,
        rowBorder: false,
        spacing: 8,
        wrapperBorder: false,
    }), []);

    const getDate = (value) => {
        const date = value && value.includes('T') ? value.split('T')[0] : value === 'string' ? '' : value;
        return Utils.formatDateObject(date);
    }

    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return (
                    <div className="wizard-step">
                        <h2>Fill in the fields below to create a taxonomy compliant name for this order. You can update this later if needed.</h2>
                        
                        {/* Taxonomy Preview */}
                        {taxonomyPreview && (
                            <div className="taxonomy-preview">
                                <div className="preview-label">Preview:</div>
                                <div className="preview-text">
                                    <i className="omni-icon-info"></i>
                                    {taxonomyPreview}
                                </div>
                            </div>
                        )}

                        {/* Dynamic Fields from campaignTaxonomyData - Grid Layout */}
                        <div className="form-fields-grid">
                            {campaignTaxonomyData?.fields?.map((field, index) => (
                                <div className="form-group" key={field.field_name || index}>
                                    <label>
                                        {field.mandatory ? '*' : ''}{field.alias || field.name}
                                    </label>
                                    
                                    {field.field_type === 1 ? (
                                        // Dropdown field
                                        <OmniDropdownComponent
                                            typeahead
                                            searchindropdown
                                            hidefooter
                                            placeholder="Please select"
                                            onDropdownChange={(ev) => handleInputChange(field.alias || field.name, ev.detail)}
                                            value={formData[field.alias || field.name] ? [formData[field.alias || field.name]] : []}
                                            options={field.fielditems?.map(item => ({
                                                value: item.name,
                                                id: item.code
                                            })) || []}
                                        />
                                    ) : (
                                        // Text input field
                                        <input
                                            type="text"
                                            value={formData[field.alias || field.name] || ''}
                                            onChange={(e) => handleInputChange(field.alias || field.name, e.target.value)}
                                            placeholder={field.alias || field.name}
                                            required={field.is_required}
                                            maxLength={field.character_limit || undefined}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                );
            case 2:
                return (
                    <div className="wizard-step">
                        <h2>Set up an order container from available planned spend.</h2>
                        
                        <div style={{ height: '400px', width: '100%', marginTop: '24px' }}>
                            <AgGridReact
                                ref={gridRef}
                                theme={channelTableTheme}
                                columnDefs={columnDefs}
                                rowData={gridChannelData}
                                defaultColDef={defaultColDef}
                                treeData={true}
                                treeDataChildrenField="nestedData"
                                getRowId={(params) => params.data.id}
                                rowSelection={{
                                    mode: "multiRow",
                                    groupSelects: "descendants",
                                    checkboxLocation: 'autoGroupColumn',
                                    hideDisabledCheckboxes: true,
                                }}
                                onGridReady={onGridReady}
                                onSelectionChanged={(event) => {
                                    const selectedNodes = event.api.getSelectedNodes();
                                    const selectedData = selectedNodes.map(node => node.data);
                                    
                                    // Store full selected data objects for API submission
                                    setSelectedChannelData(selectedData);
                                    
                                    // Calculate total from selected publishers only (not parent channels)
                                    const total = selectedData
                                        .filter(item => !item.isChannel) // Only publishers
                                        .reduce((sum, item) => sum + (item.availablePlannedSpend || 0), 0);
                                    setSelectedTotal(total);
                                }}
                                groupDefaultExpanded={-1}
                                groupDisplayType={"custom"}
                                headerHeight={40}
                                rowHeight={40}
                            />
                        </div>
                    </div>
                );
            case 3:
                return (
                    <div className="wizard-step">
                        <h2>Confirm order details. You can update this later if needed.</h2>
                        
                        <div className="form-fields-horizontal-centered">
                            <div className="form-group-horizontal">
                                <label>Client Code:</label>
                                <OmniDropdownComponent
                                    typeahead
                                    searchindropdown
                                    hidefooter
                                    placeholder="Select Client Code"
                                    onDropdownChange={(ev) => handleInputChange('clientCode', ev.detail)}
                                    value={formData.clientCode ? [formData.clientCode] : []}
                                    options={clientCodes}
                                />
                            </div>

                            <div className="form-group-horizontal">
                                <label>Date Range:</label>
                                <div className="date-range-container">
                                    <input
                                        type="date"
                                        id="start-date"
                                        className='input'
                                        value={formData.flight.startDate || ''}
                                        data-date={getDate(formData.flight.startDate) || 'Start Date'}
                                        onChange={(e) => handleFlightChange('startDate', e.target.value)}
                                        min={campaignData[0]?.start_date || ''}
                                        max={campaignData[0]?.end_date || ''}
                                    />
                                    <span className="date-separator">—</span>
                                    <input
                                        type="date"
                                        id="end-date"
                                        className='input'
                                        value={formData.flight.endDate || ''}
                                        data-date={getDate(formData.flight.endDate) || 'End Date'}
                                        onChange={(e) => handleFlightChange('endDate', e.target.value)}
                                        min={campaignData[0]?.start_date || ''}
                                        max={campaignData[0]?.end_date || ''}
                                    />
                                </div>
                            </div>

                            <div className="form-group-horizontal">
                                <label>Order Budget:</label>
                                <input
                                    type="text"
                                    className='input'
                                    value={selectedTotal.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: localCurrency && isLocal ? localCurrency : 'USD',
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    })}
                                    readOnly
                                    placeholder="$0.00"
                                />
                            </div>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    if (!isOpen) return null;

    return (
        <div className="create-media-order-wizard-overlay">
            <div className="create-media-order-wizard">
                <div className="wizard-header">
                    <h2>Set Up New Order</h2>
                    <div className="wizard-header-right">
                        <span className="step-indicator">{currentStep} of {totalSteps}</span>
                        <button className="tertiary" onClick={onClose}>
                            <OmniIconComponent icon-id="omni:interactive:close" />
                        </button>
                    </div>
                </div>

                <div className="wizard-content">
                    {renderStepContent()}
                </div>

                {currentStep === 2 && (
                    <div className="wizard-total-section">
                        <span className="total-label">Total:</span>
                        <span className="total-amount">
                            {selectedTotal.toLocaleString('en-US', {
                                style: 'currency',
                                currency: localCurrency && isLocal ? localCurrency : 'USD',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}
                        </span>
                        {/* <OmniIconComponent 
                            icon-id="omni:informative:info" 
                            style={{ marginLeft: '8px', fontSize: '16px', cursor: 'pointer', color: '#999' }}
                        /> */}
                    </div>
                )}

                <div className="wizard-footer">
                    {currentStep === 1 && (
                        <div className="wizard-actions-left">
                            <span className="required-note">* Required fields</span>
                        </div>
                    )}
                    <div className="wizard-actions-right">
                        <button className="tertiary" onClick={onClose}>
                            Cancel
                        </button>
                        {currentStep > 1 && (
                            <button 
                                className="secondary"
                                onClick={handlePrevious}
                            >
                                Back
                            </button>
                        )}
                        {currentStep < totalSteps ? (
                            <button onClick={handleNext}>
                                Next
                            </button>
                        ) : (
                            <button onClick={handleSubmit}>
                                Create Media Order
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateMediaOrderWizard;
